import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Tab, Tabs, TextField } from '@mui/material';
import { ViewMode } from './ViewMode';
import { ShipmentsTable } from './table/ShipmentsTable';
import { BankDetailsTable } from './table/BankDetailsTable';
import { AddressesTable } from './table/AddressesTable';
import { ChargesTable } from './table/ChargesTable';
import { InvoiceMessageDto } from 'src/generated/services/TFinancialApi';
import { ValidateNode } from 'src/use-cases/internalDashboard/useValidation';
import IconWarning from 'src/assets/images/icons/warning-2-red.svg';
import { ValidateInvoiceStep } from 'src/presentations/components/molecules/uploadInvoice/types';
import { InvoiceDetailsTab } from './tabs/InvoiceDetailsTab';

export enum TabViewMode {
  INVOICE_DETAILS = 0,
  SHIPMENTS = 1,
  ADDRESSES = 2,
  BANK_DETAILS = 3,
  CHARGES = 4,
}

export interface TabsProps {
  localJson: InvoiceMessageDto;
  setLocalJson: (json: InvoiceMessageDto) => void;
  isValidJson: boolean;
  setIsValidJson: (isInvalid: boolean) => void;
  validateJson: any;
  validateUpdatedJson: any;
  ocrMessage: InvoiceMessageDto;
  activeStep: ValidateInvoiceStep;
  viewMode: string;
}

export const FormValidateInvoice = ({
  ocrMessage,
  viewMode,
  isValidJson,
  setIsValidJson,
  validateJson,
  validateUpdatedJson,
  activeStep,
}: {
  ocrMessage: InvoiceMessageDto;
  viewMode: ViewMode;
  isValidJson: boolean;
  setIsValidJson: (isValid: boolean) => void;
  validateJson: ValidateNode<InvoiceMessageDto>;
  validateUpdatedJson?: ValidateNode<InvoiceMessageDto>;
  activeStep: ValidateInvoiceStep;
}) => {
  const { setValue } = useFormContext();
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [localJson, setLocalJson] = useState<InvoiceMessageDto>(ocrMessage);
  const [rawJsonShipments, setRawJsonShipments] = useState<string>();
  const [rawJsonBankDetails, setRawJsonBankDetails] = useState<string>();
  const [rawJsonCharges, setRawJsonCharges] = useState<string>();
  const [rawJsonAddresses, setRawJsonAddresses] = useState<string>();

  useEffect(() => {
    setLocalJson(ocrMessage);
  }, [ocrMessage]);

  useEffect(() => {
    const shipmentsWithoutDetails = (localJson?.shipments || []).map(({ charges, shipperAddresses, consigneeAddresses, ...rest }) => rest);
    setRawJsonShipments(JSON.stringify(shipmentsWithoutDetails, null, 2));
    setRawJsonBankDetails(JSON.stringify(localJson?.bankDetails || [], null, 2));
    setRawJsonCharges(
      JSON.stringify(
        (localJson?.shipments || []).map((s) => ({ shipmentNumber: s.shipmentNumber, charges: s.charges })),
        null,
        2
      )
    );
    setRawJsonAddresses(
      JSON.stringify(
        {
          serviceProviderAddresses: localJson?.serviceProviderAddresses || [],
          billedPartyAddresses: localJson?.billedPartyAddresses || [],
          shipperAddresses: (localJson?.shipments || []).flatMap((s) => (s.shipperAddresses || []).map((a) => ({ ...a, shipmentNumber: s.shipmentNumber }))),
          consigneeAddresses: (localJson?.shipments || []).flatMap((s) =>
            (s.consigneeAddresses || []).map((a) => ({ ...a, shipmentNumber: s.shipmentNumber }))
          ),
        },
        null,
        2
      )
    );
  }, [localJson]);

  const handleFieldChange = (field: keyof InvoiceMessageDto, value: any) => {
    const updatedJson = { ...localJson, [field]: value };
    setLocalJson(updatedJson);
  };

  const handleRawJsonShipmentsChange = (text: string) => {
    setRawJsonShipments(text);
    try {
      JSON.parse(text);
      setIsValidJson(true);
    } catch {
      setIsValidJson(false);
    }
  };

  const handleRawJsonShipmentBlur = () => {
    try {
      const parsed = JSON.parse(rawJsonShipments);
      setIsValidJson(true);
      const existingShipments = localJson?.shipments || [];
      const mergedShipments = parsed.map((shipment: any) => {
        const existing = existingShipments.find((s) => s.shipmentNumber === shipment.shipmentNumber);
        return {
          ...shipment,
          charges: shipment.charges ?? existing?.charges ?? [],
          shipperAddresses: shipment.shipperAddresses ?? existing?.shipperAddresses ?? [],
          consigneeAddresses: shipment.consigneeAddresses ?? existing?.consigneeAddresses ?? [],
        };
      });
      handleFieldChange('shipments', mergedShipments);
    } catch {
      setIsValidJson(false);
    }
  };

  const handleRawJsonBankDetailsChange = (text: string) => {
    setRawJsonBankDetails(text);
    try {
      JSON.parse(text);
      setIsValidJson(true);
    } catch {
      setIsValidJson(false);
    }
  };

  const handleRawJsonBankDetailsBlur = () => {
    try {
      const parsedData = JSON.parse(rawJsonBankDetails);
      setIsValidJson(true);
      handleFieldChange('bankDetails', parsedData);
    } catch {
      setIsValidJson(false);
    }
  };

  const handleRawJsonChargesChange = (text: string) => {
    setRawJsonCharges(text);
    try {
      JSON.parse(text);
      setIsValidJson(true);
    } catch {
      setIsValidJson(false);
    }
  };

  const handleRawJsonChargesBlur = () => {
    try {
      const parsedData = JSON.parse(rawJsonCharges);
      setIsValidJson(true);
      const updatedShipments =
        localJson.shipments?.map((shipment) => {
          const updatedShipment = parsedData.find((p: any) => p.shipmentNumber === shipment.shipmentNumber);
          if (updatedShipment) {
            return {
              ...shipment,
              charges: updatedShipment.charges,
            };
          }
          return shipment;
        }) || [];
      handleFieldChange('shipments', updatedShipments);
    } catch {
      setIsValidJson(false);
    }
  };

  const handleRawJsonAddressesChange = (text: string) => {
    setRawJsonAddresses(text);
    try {
      JSON.parse(text);
      setIsValidJson(true);
    } catch {
      setIsValidJson(false);
    }
  };

  const handleRawJsonAddressesBlur = () => {
    try {
      const parsed = JSON.parse(rawJsonAddresses);
      setIsValidJson(true);
      // update localJson with new addresses
      const updateInvoiceMessageWithAddresses = (invoiceMessage: InvoiceMessageDto, addressData: any): InvoiceMessageDto => {
        const updatedInvoiceMessage: InvoiceMessageDto = { ...invoiceMessage };
        updatedInvoiceMessage.serviceProviderAddresses = addressData.serviceProviderAddresses || [];
        updatedInvoiceMessage.billedPartyAddresses = addressData.billedPartyAddresses || [];
        if (Array.isArray(updatedInvoiceMessage.shipments)) {
          // shipper
          if (addressData.shipperAddresses) {
            const shipperMap = addressData.shipperAddresses.reduce((acc: Record<string, any[]>, addr: any) => {
              if (!addr.shipmentNumber) return acc;
              if (!acc[addr.shipmentNumber]) acc[addr.shipmentNumber] = [];
              const { shipmentNumber, ...address } = addr;
              acc[addr.shipmentNumber].push(address);
              return acc;
            }, {});
            updatedInvoiceMessage.shipments = updatedInvoiceMessage.shipments.map((s) => ({
              ...s,
              shipperAddresses: shipperMap[s.shipmentNumber] || [],
            }));
          }
          // consignee
          if (addressData.consigneeAddresses) {
            const consigneeMap = addressData.consigneeAddresses.reduce((acc: Record<string, any[]>, addr: any) => {
              if (!addr.shipmentNumber) return acc;
              if (!acc[addr.shipmentNumber]) acc[addr.shipmentNumber] = [];
              const { shipmentNumber, ...address } = addr;
              acc[addr.shipmentNumber].push(address);
              return acc;
            }, {});
            updatedInvoiceMessage.shipments = updatedInvoiceMessage.shipments.map((s) => ({
              ...s,
              consigneeAddresses: consigneeMap[s.shipmentNumber] || [],
            }));
          }
        }
        return updatedInvoiceMessage;
      };
      const updated = updateInvoiceMessageWithAddresses(localJson, parsed);
      setLocalJson(updated);
    } catch {
      setIsValidJson(false);
    }
  };

  const renderShipments = () =>
    viewMode === 'structured' ? (
      <ShipmentsTable
        shipments={localJson?.shipments || []}
        onUpdate={(shipments) => handleFieldChange('shipments', shipments)}
        validateJson={validateJson}
        validateUpdatedJson={validateUpdatedJson}
        activeStep={activeStep}
        ocrMessage={ocrMessage}
        isValidJson={isValidJson}
        setIsValidJson={setIsValidJson}
      />
    ) : (
      <TextField
        multiline
        fullWidth
        rows={20}
        label='Shipments Data'
        value={rawJsonShipments}
        onChange={(e) => handleRawJsonShipmentsChange(e.target.value)}
        onBlur={handleRawJsonShipmentBlur}
        error={!isValidJson}
        helperText={!isValidJson ? 'Invalid JSON' : ''}
        sx={{
          '& .MuiInputBase-input': {
            fontFamily: 'IBM Plex Mono, monospace',
            fontSize: '12px',
          },
          '& .MuiInputLabel-root': {
            fontFamily: 'IBM Plex Mono, monospace',
            fontSize: '12px',
          },
        }}
      />
    );

  const renderAddresses = () =>
    viewMode === 'structured' ? (
      <AddressesTable
        invoiceMessage={localJson || ({} as InvoiceMessageDto)}
        onUpdate={(updatedMessage) => {
          setLocalJson(updatedMessage);
        }}
        validateJson={validateJson}
        validateUpdatedJson={validateUpdatedJson}
        ocrMessage={ocrMessage}
        activeStep={activeStep}
        isValidJson={isValidJson}
        setIsValidJson={setIsValidJson}
      />
    ) : (
      <TextField
        multiline
        fullWidth
        rows={20}
        label='Addresses Data'
        value={rawJsonAddresses}
        onChange={(e) => handleRawJsonAddressesChange(e.target.value)}
        error={!isValidJson}
        helperText={!isValidJson ? 'Invalid JSON' : ''}
        onBlur={handleRawJsonAddressesBlur}
        sx={{
          '& .MuiInputBase-input': {
            fontFamily: 'IBM Plex Mono, monospace',
            fontSize: '12px',
          },
          '& .MuiInputLabel-root': {
            fontFamily: 'IBM Plex Mono, monospace',
            fontSize: '12px',
          },
        }}
      />
    );

  const renderBankDetails = () =>
    viewMode === 'structured' ? (
      <BankDetailsTable
        bankDetails={localJson?.bankDetails || []}
        onUpdate={(bankDetails) => handleFieldChange('bankDetails', bankDetails)}
        validateJson={validateJson}
        ocrMessage={ocrMessage}
        validateUpdatedJson={validateUpdatedJson}
        activeStep={activeStep}
      />
    ) : (
      <TextField
        multiline
        fullWidth
        rows={20}
        label='Bank Details Data'
        value={rawJsonBankDetails}
        onChange={(e) => handleRawJsonBankDetailsChange(e.target.value)}
        onBlur={handleRawJsonBankDetailsBlur}
        error={!isValidJson}
        helperText={!isValidJson ? 'Invalid JSON' : ''}
        sx={{
          '& .MuiInputBase-input': {
            fontFamily: 'IBM Plex Mono, monospace',
            fontSize: '12px',
          },
          '& .MuiInputLabel-root': {
            fontFamily: 'IBM Plex Mono, monospace',
            fontSize: '12px',
          },
        }}
      />
    );

  const renderCharges = () =>
    viewMode === 'structured' ? (
      <ChargesTable
        invoiceMessage={localJson}
        onUpdate={(updatedMessage) => {
          setLocalJson(updatedMessage);
        }}
        validateJson={validateJson}
        validateUpdatedJson={validateUpdatedJson}
        ocrMessage={ocrMessage}
        activeStep={activeStep}
      />
    ) : (
      <TextField
        multiline
        fullWidth
        rows={20}
        label='Charges Data'
        value={rawJsonCharges}
        onChange={(e) => handleRawJsonChargesChange(e.target.value)}
        onBlur={handleRawJsonChargesBlur}
        error={!isValidJson}
        helperText={!isValidJson ? 'Invalid JSON' : ''}
        sx={{
          '& .MuiInputBase-input': {
            fontFamily: 'IBM Plex Mono, monospace',
            fontSize: '12px',
          },
          '& .MuiInputLabel-root': {
            fontFamily: 'IBM Plex Mono, monospace',
            fontSize: '12px',
          },
        }}
      />
    );

  useEffect(() => {
    const data = localJson;
    if (data) {
      setValue('invoiceData', JSON.stringify(data, null, 2));
    } else {
      setValue('invoiceData', '');
    }
  }, [localJson, setValue]);

  const tabErrors = {
    invoice: () => validateJson?.['exception']?.['error'] || '',
    shipments: () => {
      if (Array.isArray(validateJson?.shipments?.['value'])) {
        for (const s of validateJson.shipments['value']) {
          if (s?.['isValid'] === false) {
            return s?.['exception']?.['error'] || '';
          }
        }
      }
      return '';
    },
    addresses: () => {
      // Helper to check for invalid entries in an array
      const findInvalidError = (arr: any[]) => {
        if (!Array.isArray(arr)) return '';
        for (const item of arr) {
          if (item?.['isValid'] === false) {
            return item?.['exception']?.['error'] || '';
          }
        }
        return '';
      };

      // Helper to check nested address arrays in shipments
      const findInvalidInShipmentAddresses = (shipmentsArr: any[], addressKey: string) => {
        if (!Array.isArray(shipmentsArr)) return '';
        for (const shipment of shipmentsArr) {
          const error = findInvalidError(shipment?.[addressKey]?.['value'] || []);
          if (error) return error;
        }
        return '';
      };

      let error = findInvalidError(validateJson?.serviceProviderAddresses?.['value']);
      if (error) return error;
      error = findInvalidError(validateJson?.billedPartyAddresses?.['value']);
      if (error) return error;
      error = findInvalidInShipmentAddresses(validateJson?.shipments?.['value'], 'shipperAddresses');
      if (error) return error;
      error = findInvalidInShipmentAddresses(validateJson?.shipments?.['value'], 'consigneeAddresses');
      if (error) return error;
      return '';
    },
    bankDetails: () => {
      if (Array.isArray(validateJson?.bankDetails?.['value'])) {
        for (const s of validateJson?.bankDetails['value'] ?? []) {
          if (s?.['isValid'] === false) {
            return s?.['exception']?.['error'] || '';
          }
        }
      }
      return '';
    },
    charges: () => {
      if (Array.isArray(validateJson?.shipments?.['value'])) {
        for (const s of validateJson?.shipments?.['value'] ?? []) {
          if (Array.isArray(s?.charges?.['value'])) {
            for (const c of s?.charges?.['value'] ?? []) {
              if (c?.['isValid'] === false) {
                return c?.['exception']?.['error'] || '';
              }
            }
          }
        }
      }
      return '';
    },
  };

  const tabConfigs = [
    {
      label: 'Invoice',
      error: tabErrors.invoice(),
    },
    {
      label: 'Shipments',
      error: tabErrors.shipments(),
    },
    {
      label: 'Addresses',
      error: tabErrors.addresses(),
    },
    {
      label: 'Bank Details',
      error: tabErrors.bankDetails(),
    },
    {
      label: 'Charges',
      error: tabErrors.charges(),
    },
  ];

  return (
    <div className='pt-12'>
      <Tabs
        value={tabIndex}
        onChange={(_, newIndex) => {
          if (!isValidJson) return; // Prevent tab change if JSON is invalid
          setTabIndex(newIndex);
        }}
      >
        {tabConfigs.map((tab, idx) => (
          <Tab
            key={tab.label}
            label={
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {tab.label}
                {tab.error && (
                  <img
                    src={IconWarning}
                    alt='warning icon'
                    style={{ verticalAlign: 'middle', position: 'relative', marginLeft: 5, width: 16, height: 16 }}
                    title={tab.error}
                  />
                )}
              </span>
            }
          />
        ))}
      </Tabs>
      <div className='pt-12'>
        {tabIndex === TabViewMode.INVOICE_DETAILS && (
          <InvoiceDetailsTab
            localJson={localJson}
            setLocalJson={setLocalJson}
            isValidJson={isValidJson}
            setIsValidJson={setIsValidJson}
            validateJson={validateJson}
            validateUpdatedJson={validateUpdatedJson}
            ocrMessage={ocrMessage}
            activeStep={activeStep}
            viewMode={viewMode}
          />
        )}
        {tabIndex === TabViewMode.SHIPMENTS && renderShipments()}
        {tabIndex === TabViewMode.ADDRESSES && renderAddresses()}
        {tabIndex === TabViewMode.BANK_DETAILS && renderBankDetails()}
        {tabIndex === TabViewMode.CHARGES && renderCharges()}
      </div>
    </div>
  );
};
